/**
 * Encryption at rest for the device store (WebCrypto only; nothing hand-rolled).
 *
 * - One random data key (DEK, AES-GCM-256) per device encrypts every record.
 * - The DEK is stored only WRAPPED: once under each enrolled user's PIN-derived key
 *   (PBKDF2-SHA-256), and once under the server-held device escrow key, which the server hands to a
 *   user who signs in online. So a forgotten PIN, or a second user's first sign-in, needs the
 *   internet, and a revoked device can never be opened again by a new sign-in.
 * - Every ciphertext is bound to its row and purpose (AES-GCM additional data), so ciphertexts
 *   cannot be swapped between records.
 *
 * LIMITS, stated plainly: a 6-digit PIN protects against casual access to a lost tablet. Someone who
 * copies the browser's storage files can try all PINs offline; PBKDF2 slows that down but cannot stop
 * it. Hardware-backed keys (Android Keystore via Capacitor) are the planned upgrade.
 */
const subtle = () => {
    const s = globalThis.crypto?.subtle;
    if (!s)
        throw new Error('WebCrypto is not available (needs a secure context: https or the app)');
    return s;
};
export const DEFAULT_PBKDF2_ITERATIONS = 300_000;
const enc = new TextEncoder();
const dec = new TextDecoder();
export const randomBytes = (n) => globalThis.crypto.getRandomValues(new Uint8Array(new ArrayBuffer(n)));
export function b64ToBytes(b64) {
    const bin = atob(b64);
    const out = new Uint8Array(new ArrayBuffer(bin.length));
    for (let i = 0; i < bin.length; i++)
        out[i] = bin.charCodeAt(i);
    return out;
}
const DEK_USAGES = ['encrypt', 'decrypt', 'wrapKey', 'unwrapKey'];
/** The DEK can also wrap keys: during key rotation the OLD key is kept wrapped under the NEW one. */
export function generateDek() {
    return subtle().generateKey({ name: 'AES-GCM', length: 256 }, true, DEK_USAGES);
}
export async function deriveKek(pin, salt, iterations) {
    const material = await subtle().importKey('raw', enc.encode(pin), 'PBKDF2', false, ['deriveKey']);
    return subtle().deriveKey({ name: 'PBKDF2', hash: 'SHA-256', salt, iterations }, material, { name: 'AES-GCM', length: 256 }, false, ['wrapKey', 'unwrapKey']);
}
/** The server's 32-byte device escrow key as a wrapping key. */
export function importEscrowKey(b64) {
    const raw = b64ToBytes(b64);
    if (raw.length !== 32)
        throw new Error('Device key must be 32 bytes');
    return subtle().importKey('raw', raw, { name: 'AES-GCM' }, false, ['wrapKey', 'unwrapKey']);
}
export async function wrapKey(dek, kek) {
    const iv = randomBytes(12);
    const data = await subtle().wrapKey('raw', dek, kek, { name: 'AES-GCM', iv });
    return { iv, data };
}
/** Throws if `kek` is not the key the DEK was wrapped with (AES-GCM authentication fails). */
export function unwrapKey(w, kek) {
    return subtle().unwrapKey('raw', w.data, kek, { name: 'AES-GCM', iv: w.iv }, { name: 'AES-GCM', length: 256 }, true, DEK_USAGES);
}
export async function seal(dek, value, aad) {
    const iv = randomBytes(12);
    const ct = await subtle().encrypt({ name: 'AES-GCM', iv, additionalData: enc.encode(aad) }, dek, enc.encode(JSON.stringify(value)));
    return { iv, ct };
}
export async function open(dek, s, aad) {
    const pt = await subtle().decrypt({ name: 'AES-GCM', iv: s.iv, additionalData: enc.encode(aad) }, dek, s.ct);
    return JSON.parse(dec.decode(pt));
}
/** PIN rules: 6-12 digits, not all the same digit, not a straight run (123456, 987654). */
export function pinProblem(pin) {
    if (!/^\d{6,12}$/.test(pin))
        return 'PIN must be 6 to 12 digits';
    if (/^(\d)\1+$/.test(pin))
        return 'PIN cannot be one digit repeated';
    const d = [...pin].map(Number);
    const step = (d[1] ?? 0) - (d[0] ?? 0);
    if (Math.abs(step) === 1 && d.every((x, i) => i === 0 || x - (d[i - 1] ?? 0) === step)) {
        return 'PIN cannot be a straight run like 123456';
    }
    return null;
}
//# sourceMappingURL=crypto.js.map